import React from 'react';
import { Box, Container, Grid, Typography, Link } from '@mui/material';
import { PRIMARY, PRIMARY_GRADIENT } from '../colors';

function Footer({ onAbout, simple = false }) {
  if (simple) {
    // Minimal footer for authenticated pages - dark theme
    return (
      <Box sx={{ 
        background: 'rgba(255, 255, 255, 0.02)',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        py: 3, 
        mt: 'auto' 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
              © {new Date().getFullYear()} FleetPulse. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Link 
                href="/privacy.html" 
                target="_blank" 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  textDecoration: 'none', 
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Privacy
              </Link>
              <Link 
                href="/terms.html" 
                target="_blank" 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  textDecoration: 'none', 
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Terms
              </Link>
              <Link 
                href="/sms-consent.html" 
                target="_blank" 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  textDecoration: 'none', 
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                SMS Consent
              </Link>
              {onAbout && (
                <Link 
                  component="button" 
                  onClick={onAbout} 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    textDecoration: 'none', 
                    transition: 'color 0.2s',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    '&:hover': { color: PRIMARY } 
                  }}
                >
                  About
                </Link>
              )}
              <Link 
                href="mailto:support@myfleetpulse.app?subject=FleetPulse Support" 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  textDecoration: 'none', 
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Support
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  // Full footer for landing page - dark theme
  return (
    <Box 
      sx={{ 
        background: 'linear-gradient(180deg, #0a0a0f 0%, #05050a 100%)',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        color: 'white', 
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background gradient orb */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '-200px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '400px',
          background: `radial-gradient(ellipse, ${PRIMARY}1a 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: PRIMARY_GRADIENT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 24px ${PRIMARY}4d`,
                }}
              >
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/logo.jpg`}
                  alt="FleetPulse"
                  style={{ width: 32, height: 32, borderRadius: 6 }}
                />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  background: PRIMARY_GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                FleetPulse
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 3, maxWidth: 280 }}>
              Vehicle maintenance tracking for owners and fleet teams.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
              © {new Date().getFullYear()} FleetPulse. All rights reserved.
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>Product</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link 
                href="#features" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Features
              </Link>
              <Link 
                href="#pricing" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Pricing
              </Link>
              <Link 
                href="#faq" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                FAQ
              </Link>
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>Company</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {onAbout && (
                <Link 
                  component="button" 
                  onClick={onAbout} 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.6)', 
                    textDecoration: 'none', 
                    fontSize: '0.9rem',
                    transition: 'color 0.2s',
                    background: 'none', 
                    border: 'none', 
                    cursor: 'pointer', 
                    textAlign: 'left', 
                    padding: 0,
                    '&:hover': { color: PRIMARY } 
                  }}
                >
                  About
                </Link>
              )}
              <Link 
                href="mailto:support@myfleetpulse.app?subject=FleetPulse Inquiry" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Contact
              </Link>
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>Legal</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link 
                href="/privacy.html" 
                target="_blank" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Privacy Policy
              </Link>
              <Link 
                href="/terms.html" 
                target="_blank" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Terms of Service
              </Link>
              <Link 
                href="/sms-consent.html" 
                target="_blank" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                SMS Consent
              </Link>
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>Support</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link 
                href="mailto:support@myfleetpulse.app?subject=FleetPulse Support Request" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Email Support
              </Link>
              <Link 
                href="#faq" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: PRIMARY } 
                }}
              >
                Help Center
              </Link>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Footer;
